import { decorate, observable, action, computed, toJS, set } from 'mobx';
import { uniqBy, findIndex, groupBy, concat } from 'lodash';

import { buildURL } from 'modules/api-fetch';
import Config from 'modules/config';

import { forceResendWorkout } from 'modules/functions/src/resendWorkouts';
import loadCacheFromStorage from './loadCacheFromStorage';
import saveCacheEntryToStorage from './saveCacheEntryToStorage';
import deleteCacheEntryFromStorage from './deleteCacheEntryFromStorage';

type OneOfOneOfAllDataTypes = import('modules/typescript').OneOfOneOfAllDataTypes;
type WorkoutLog = import('modules/typescript').WorkoutLog;
type FetchCacheStoredObject = import('modules/with-fetch').FetchCacheStoredObject;
type query = object | undefined;

type FetchCacheMap = { [key: string]: FetchCacheStoredObject };

class FetchCacheStore {
  fetchResults: FetchCacheMap = {};

  constructor() {
    // No need to await
    loadCacheFromStorage();
  }

  setCached(
    url: string,
    query: object | undefined,
    data: OneOfOneOfAllDataTypes[],
    { protectLocalData = false } = {},
  ): void {
    // Preserve any dirty results from our old cache
    const cacheResults = this.getCached(url, query);

    let combinedData = data;

    // ALSO prevent the api call from over-riding any local versions where the version number is higher than the server version
    // Basically just keeps any existing local items (workouts) with higher versions numbers
    if (cacheResults && protectLocalData) {
      combinedData = this.protectLocalDataVersion(cacheResults, data);
    }
    // Concat anything marked as 'pending' to our cache
    // if (
    //   concatPending &&
    //   cacheResults &&
    //   cacheResults.data &&
    //   cacheResults.data.length > 0
    // ) {
    //   const pendingEntries =
    //     cacheResults.data.filter(
    //       (entry: OneOfOneOfAllDataTypes) => entry.isPending,
    //     ) || [];
    //   // console.devLog.info('Pending entries:', pendingEntries);
    //   combinedData = concat(data, pendingEntries);
    //   combinedData = uniqBy(combinedData, 'id');
    // }

    const fullURL = buildURL({ url, query });
    const now = new Date();

    const value = { timestamp: now.getTime(), data: combinedData };

    this.fetchResults[fullURL] = value;
    // No need to await this
    saveCacheEntryToStorage(fullURL, value);
  }

  setFetchResults = (key: string, value: FetchCacheStoredObject): void => {
    this.fetchResults[key] = value;
  };

  removeCached(url: string, query: query): void {
    const fullURL = buildURL({ url, query });
    // console.devLog.info('Removing all cached items for:', fullURL);
    delete this.fetchResults[fullURL];
    deleteCacheEntryFromStorage(fullURL);
    // saveCacheEntryToStorage(fullURL, null);
  }

  removeSingleEntryFromCache(
    url: string,
    query: query = undefined,
    id: number,
  ): void {
    // We need to get the entire result set from this call, remove the single item we want from it via ID, and then set that filtered result into the cache
    const cacheResults = this.getCached(url, query);

    if (!cacheResults) {
      throw new Error(
        `Removing single entry form fetch cache failed to find any entry for:${url}`,
      );
    }

    const entryIndex = findIndex(cacheResults.data, (entry) => entry.id === id);

    if (entryIndex < 0) {
      throw new Error(
        `Removing single entry form fetch cache failed to find a matching index entry for:${url}`,
      );
    }

    cacheResults.data.splice(entryIndex, 1);

    this.setCached(url, query, cacheResults.data);
  }

  getSingleEntry = (
    url: string,
    query: query = undefined,
    item: OneOfOneOfAllDataTypes,
  ): OneOfOneOfAllDataTypes | null => {
    const cacheResults = this.getCached(url, query);

    if (!cacheResults) {
      throw new Error(
        `Get Single Entry in FCS failed to find an entry for url: ${url} query: ${JSON.stringify(
          query,
        )}`,
      );
    }
    const entryIndex = findIndex(
      cacheResults.data,
      (entry) => entry.id === item.id,
    );

    if (entryIndex < 0) {
      throw new Error(
        `Get Single Entry in FCS failed to find a matching entry for index:': ${entryIndex} for URL:  ${url} query: ${JSON.stringify(
          query,
        )}`,
      );
    }

    return cacheResults.data[entryIndex];
  };

  updateSingleEntry = (
    url: string,
    query: query = undefined,
    newEntry: OneOfOneOfAllDataTypes,
  ): void => {
    const cacheResults = this.getCached(url, query);

    // Update the specific one we need...

    if (!cacheResults) {
      throw new Error(
        `updateSingleEntry inside FCS failed to find cached results for: ${url} query: ${JSON.stringify(
          query,
        )}`,
      );
    }

    const entryIndex = findIndex(
      cacheResults.data,
      (entry) => entry.id === newEntry.id,
    );

    if (entryIndex < 0 && newEntry.id < 0) {
      console.devLog.info('Inserting:', newEntry);
      // This is a workout template we are creating a workout log for. Insert it
      cacheResults.data.push(newEntry);
      return;
    }

    if (entryIndex < 0) {
      throw new Error(
        `updateSingleEntry inside FCS failed to find a matching index for: ${entryIndex} or url:  ${url} query: ${JSON.stringify(
          query,
        )}`,
      );
    }

    // We directly mutate the entry in the object here, only changing what is needed
    set(cacheResults.data[entryIndex], newEntry);
  };

  updateSingleProperty = (
    url: string,
    query: query = undefined,
    id: number,
    key: string,
    value: any,
  ): void => {
    const cacheResults = this.getCached(url, query);

    if (!cacheResults) {
      throw new Error('updateSingleProperty failed to find cached results');
    }
    // Find item by ID
    const entryIndex = findIndex(cacheResults.data, (entry) => entry.id === id);
    if (entryIndex < 0) {
      throw new Error(
        'updateSingleProperty failed to find a matching result in cached results',
      );
    }

    // Update the value for this entry
    cacheResults.data[entryIndex][key] = value;
  };

  // When we pass an OLD data set and a NEW data set into this, the goal is to get back one data set, combined
  // However, we want to make sure that no old ("local" workout with a higher version is over-ridden)
  protectLocalDataVersion = (
    oldData: FetchCacheStoredObject,
    newData: OneOfOneOfAllDataTypes[],
  ): OneOfOneOfAllDataTypes[] => {
    // console.devLog.info('Old data??', oldData);
    // console.devLog.info('New data??', newData);

    if (!Config.IS_MOBILE_APP) {
      return newData;
    }
    if (
      !oldData ||
      !oldData.data ||
      oldData.data.length <= 0 ||
      !newData ||
      newData.length <= 0
    ) {
      return newData;
    }

    /*
    Use a key map to avoid double nested loop
With the id bring the key
Take all the old data, create key map from it
Then iterate through new data, see if an entry exists in old data map, if so do your logic
*/

    const oldDataGrouped = groupBy(oldData.data, 'id');
    // console.devLog.info('Old data grouped:', oldDataGrouped);
    const toReturn = [] as OneOfOneOfAllDataTypes[];
    newData.forEach((newItem) => {
      if (oldDataGrouped[newItem.id]) {
        const oldItem = oldDataGrouped[newItem.id][0];

        // The 'newItem.version' is a side effect item that lets us check if the item is of type WorkoutLog and handle it accordingly
        if (
          newItem.id === oldItem.id &&
          // @ts-ignore hacky side effect for a property that only exists on WorkoutLogs at this time
          newItem.version &&
          oldItem.version &&
          // @ts-ignore hacky side effect for a property that only exists on WorkoutLogs at this time
          oldItem.version > newItem.version
        ) {
          // Do the stuff - we are using a SIDE EFFECT of workouts structure where they have isComplete to protect old workouts
          // Its a workout - send the local version back up to the server

          if (oldItem.isComplete || oldItem.isComplete === false) {
            forceResendWorkout(oldItem);
            toReturn.push(oldItem);
          }
        } else {
          toReturn.push(newItem);
        }
      } else {
        toReturn.push(newItem);
      }
    });

    return toReturn;
  };

  concatToCached(
    url: string,
    query: query,
    data: OneOfOneOfAllDataTypes[],
    filterByUniqId: boolean,
    protectLocalData: boolean = false,
  ): void {
    // If we have data, concat it, otherwise concat to empty array
    const oldCache = this.getCached(url, query);

    const oldData = oldCache ? oldCache.data : [];

    // Make sure that we can't over-ride old data with a higher version with a newer version
    // This should only happen on an actual API call now, not any local replacements

    let combinedData = concat(data, oldData);

    // console.devLog.info('Combined data..', toJS(combinedData));
    // If the flag is set, ensure no items are in there with duplicated ids.
    // This preserves earlier ones, discarding later duplicates
    // The items in 'data' will override the ones in oldCache if present

    if (filterByUniqId) {
      combinedData = uniqBy(combinedData, 'id');
    }

    this.setCached(url, query, combinedData, { protectLocalData });

    const duplicateEntryCount =
      oldData.length + data.length - combinedData.length;

    if (duplicateEntryCount > 0) {
      // console.devLog.info(
      //   `Removed ${duplicateEntryCount} duplicate entries from fetch cache`,
      // );
    }
  }

  // Using the example from https://github.com/mobxjs/mobx/issues/1388#issuecomment-372112554
  get getCache(): FetchCacheMap {
    return this.fetchResults;
  }

  getCached(url: string, query: query): FetchCacheStoredObject | undefined {
    const fullURL = buildURL({ url, query });
    const cache = this.getCache[fullURL];
    // console.devLog.info('Get cached for:', fullURL, 'and cache is:', toJS(cache));

    // We didn't get anything from cache, return undefined
    if (cache == null) {
      return undefined;
    }

    return cache;
  }

  wipeCache = (): void => {
    this.fetchResults = {};
  };
}
// @ts-ignore
decorate(FetchCacheStore, {
  fetchResults: observable,
  setCached: action,
  protectLocalDataVersion: action,
  updateSingleEntry: action,
  addSingleEntryToCache: action,
  getSingleEntry: action,
  removeCached: action,
  updateSingleProperty: action,
  concatToCached: action,
  getCache: computed,
  invalidateCacheForEndpoint: action,
  removeSingleEntryFromCache: action,
  setFetchResults: action,
  wipeCache: action,
});

const WrappedStore = FetchCacheStore;

if (Config.ENV === 'development') {
  // eslint-disable-next-line global-require
  // const remotedev = require('mobx-remotedev');
  // WrappedStore = remotedev(FetchCacheStore, {
  //   name: 'FetchCacheStore',
  //   remote: true,
  // });
}

const fetchCacheStore = new WrappedStore();

export default fetchCacheStore;
